import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import { api, setUnauthorizedHandler } from "../api/client.ts"
import type { User } from "../api/types.ts"
import { useI18n } from "../i18n/index.tsx"

type SessionValue = {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** 서버가 이미 세션을 끊은 뒤(비밀번호 변경) 화면만 로그인으로 되돌린다. */
  endSession: () => void
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const { setLocale } = useI18n()

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
  }, [])

  useEffect(() => {
    let cancelled = false
    api
      .get<{ user: User }>("/api/auth/me")
      .then((res) => {
        if (cancelled) return
        setUser(res.user)
        setLocale(res.user.locale)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [setLocale])

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.post<{ user: User }>("/api/auth/login", { email, password })
      setUser(res.user)
      setLocale(res.user.locale)
    },
    [setLocale],
  )

  const logout = useCallback(async () => {
    await api.post("/api/auth/logout")
    setUser(null)
  }, [])

  const endSession = useCallback(() => setUser(null), [])

  const value = useMemo<SessionValue>(
    () => ({ user, loading, login, logout, endSession }),
    [user, loading, login, logout, endSession],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error("SessionProvider 안에서만 쓸 수 있습니다")
  return value
}
